/**
 * ProtectedRoute — Guards routes behind authentication.
 *
 * If not authenticated, redirects to /auth/login.
 * If authenticated but user is inactive, shows deactivated message.
 * Optional role restriction: only specified roles can access.
 *
 * Usage:
 *   <Route element={<ProtectedRoute />}> ... child routes ... </Route>
 *   <Route element={<ProtectedRoute requiredRoles={['director', 'manager']} />}> ... </Route>
 */

import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { SkeletonPage } from '@/components/shared/SkeletonPage';
import type { UserRole } from '@/types';

interface ProtectedRouteProps {
  readonly requiredRoles?: readonly UserRole[];
}

export function ProtectedRoute({ requiredRoles }: ProtectedRouteProps): React.ReactElement {
  const { isAuthenticated, loading, profile } = useAuthContext();
  const location = useLocation();

  // Still checking session — show skeleton, not spinner
  if (loading) {
    return <SkeletonPage />;
  }

  // Not authenticated — redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // Authenticated but no profile loaded yet — skeleton
  if (!profile) {
    return <SkeletonPage />;
  }

  // User has been deactivated
  if (!profile.is_active) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-surface-page dark:bg-dark-page">
        <div className="glass-card p-8 text-center max-w-md animate-spring">
          <h2 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-2">
            Account Deactivated
          </h2>
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            Your account has been deactivated. Please contact your administrator.
          </p>
        </div>
      </div>
    );
  }

  // Role restriction check
  if (requiredRoles && requiredRoles.length > 0) {
    if (!requiredRoles.includes(profile.role)) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-surface-page dark:bg-dark-page">
          <div className="glass-card p-8 text-center max-w-md animate-spring">
            <h2 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-2">
              Access Restricted
            </h2>
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
              You don&apos;t have permission to access this page.
            </p>
          </div>
        </div>
      );
    }
  }

  return <Outlet />;
}
